import React, { useCallback, useEffect } from "react";
import { CounterBadge, Body1, Image, Subtitle2, Text } from "@fluentui/react-components";
import { Cart, OnlineMenuItem } from "@dining/api-client";
import { Link, useParams } from "react-router-dom";
import placeHolderImage from "../../../images/diningPlaceHolderImage.png";
import { HelperUtils, appInsightsEvents, trackEvent, useAppStore } from "../../../common";
import { useStyles } from "./MenuItemCardStyles";

interface MenuItemCardProps {
  menuItem: OnlineMenuItem;
}

export const MenuItemCard = ({ menuItem }: MenuItemCardProps) => {
  const styles = useStyles();
  const [context, cart] = useAppStore((state) => [state.context, state.cart]);
  const appInsights = useAppStore((state) => state.appInsights);
  const urlParams = useParams();

  const { value: currentCart } = cart;
  const cafeId = urlParams.cafeId;
  const stationId = urlParams.stationId;

  const dietaryLabelsStr = menuItem.dietaryLabels?.join(", ") ?? "";

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "MenuItemCard",
      cafeId,
      stationId,
      menuItemId: menuItem.id,
      menuItemName: menuItem.name,
      dietaryLabels: dietaryLabelsStr,
    });
  }, [appInsights, context, cafeId, stationId, menuItem.id, menuItem.name, dietaryLabelsStr]);

  const pricingStr: string = HelperUtils.getInstance().getPricingStr(menuItem.pricing);
  const calorieStr: string = HelperUtils.getInstance().getCalorieStr(menuItem);
  const dietaryLabelStr: string = HelperUtils.getInstance().getDietaryLabelStr(menuItem);
  const dietaryLabelAccessibleStr: string =
    HelperUtils.getInstance().getDietaryLabelAccessibleStr(menuItem);

  const onClick = useCallback(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.MenuItemSelected, context, {
      selectedCafeId: cafeId,
      selectedStationId: stationId,
      selectedMenuItem: menuItem.id,
    });
  }, [appInsights, context, cafeId, stationId, menuItem.id]);

  const isItemInCart = useCallback(
    (cart: Cart) => {
      return cart.items.some((item) => item.itemId === menuItem.id);
    },
    [menuItem.id],
  );

  const getItemCountInCart = useCallback(
    (cart: Cart) => {
      return cart.items.reduce((acc, item) => {
        if (item.itemId === menuItem.id) {
          return (acc += 1);
        }
        return acc;
      }, 0);
    },
    [menuItem.id],
  );
  const itemCountInCart = currentCart ? getItemCountInCart(currentCart) : 0;
  const accessibleLabel = [
    menuItem.name,
    dietaryLabelAccessibleStr,
    menuItem.description,
    pricingStr,
    calorieStr,
    itemCountInCart > 0
      ? `${itemCountInCart} ${itemCountInCart === 1 ? "item" : "items"} in cart`
      : "",
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <Link
      to={`/cafe/${cafeId}/station/${stationId}/item/${menuItem.id}`}
      aria-label={accessibleLabel}
      data-testid={`menuItemCard-${menuItem.id}`}
      className={styles.container}
      onClick={onClick}
    >
      <div className={styles.imageContainer}>
        <Image
          className={itemCountInCart > 0 ? styles.imageBorder : styles.image}
          src={menuItem.imageUrl ?? placeHolderImage}
          onError={(e) => {
            e.currentTarget.src = placeHolderImage;
          }}
          block={true}
          fit="contain"
          alt=""
        />
        {currentCart && isItemInCart(currentCart) && (
          <CounterBadge
            className={styles.badge}
            count={getItemCountInCart(currentCart)}
            appearance="filled"
            aria-hidden="true"
          />
        )}
      </div>
      <div className={styles.body}>
        <Subtitle2 className={styles.itemName} title={menuItem.name}>
          {menuItem.name}
          {dietaryLabelStr && (
            <Text className={styles.dietaryLabel} aria-hidden="true" data-testid="dietaryLabel">
              {" "}
              {dietaryLabelStr}
            </Text>
          )}
        </Subtitle2>
        <Body1 wrap={false} className={styles.description} truncate>
          {menuItem.description}
        </Body1>
        <div className={styles.price}>
          {pricingStr && (
            <Subtitle2 className={styles.priceString} data-testid="menuItemPrice">
              {pricingStr}
            </Subtitle2>
          )}
          {calorieStr &&
            (pricingStr ? (
              <Subtitle2 className={styles.calorieString} data-testid="menuItemCaloriesWithMidDot">
                {" "}
                &middot; {calorieStr}
              </Subtitle2>
            ) : (
              <Text className={styles.calorieString} data-testid="menuItemCalories">
                {calorieStr}
              </Text>
            ))}
        </div>
      </div>
    </Link>
  );
};
